/**
 * KYC queue (owner product review §33): counters, level/status filters and a case table ordered by
 * waiting time (oldest first). Opening a case goes to the customer's KYC tab, where the decisions are
 * taken through the OD-38 confirmation flow.
 */
import {
  AppContainer,
  Badge,
  DataTable,
  formatRelative,
  KpiTile,
  PageHeader,
  Pagination,
  SelectField,
  StateView,
} from '@hopex/ui';
import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { dt, type KycQueue, type KycQueueRow } from '../api.ts';
import { KYC1_STATUS, LEVEL_LABEL, reasonLabel } from '../lib/labels.ts';
import { Labeled } from '../lib/ui.tsx';
import { Remote, useRemote } from '../lib/remote.tsx';

const n = (v: number) => <bdi className="hx-num">{v.toLocaleString('en-US')}</bdi>;

export function KycQueuePage() {
  const navigate = useNavigate();
  const [level, setLevel] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const p = new URLSearchParams();
  if (level) p.set('level', level);
  if (status) p.set('status', status);
  p.set('page', String(page));
  const r = useRemote<KycQueue>(`/kyc/queue?${p.toString()}`);
  return (
    <AppContainer>
      <PageHeader title="صف احراز هویت" />
      <Remote r={r}>
        {(q) => (
          <div className="flex flex-col gap-4" data-kyc-queue>
            <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-5">
              <KpiTile
                label="در انتظار بررسی"
                value={n(q.counters.kycPending)}
                tone={q.counters.kycPending ? 'caution' : 'neutral'}
              />
              <KpiTile label="نیازمند اصلاح" value={n(q.counters.kycNeedsCorrection)} />
              <KpiTile
                label="خطای سرویس"
                value={n(q.counters.kycProviderUnavailable)}
                tone={q.counters.kycProviderUnavailable ? 'negative' : 'neutral'}
              />
              <KpiTile label="تصمیم دستی امروز" value={n(q.counters.kycManualDecisionsToday)} />
              <KpiTile
                label="تکمیل‌شده امروز"
                value={n(q.counters.kycCompletedToday)}
                tone="positive"
              />
            </div>
            <div className="grid max-w-xl grid-cols-2 gap-2">
              <SelectField
                label="سطح"
                value={level}
                onChange={(v) => (setLevel(v), setPage(1))}
                options={[
                  { value: '', label: 'همه' },
                  { value: '1', label: 'سطح ۱' },
                  { value: '2', label: 'سطح ۲' },
                ]}
              />
              <SelectField
                label="وضعیت"
                value={status}
                onChange={(v) => (setStatus(v), setPage(1))}
                options={[
                  { value: '', label: 'همه' },
                  { value: 'CHECKING', label: 'در حال بررسی' },
                  { value: 'PENDING', label: 'در انتظار بررسی (سطح ۲)' },
                  { value: 'NEEDS_CORRECTION', label: 'نیازمند اصلاح' },
                  { value: 'TEMPORARILY_UNAVAILABLE', label: 'خطای سرویس' },
                  { value: 'REJECTED', label: 'ردشده' },
                ]}
              />
            </div>
            <DataTable<KycQueueRow>
              label="پرونده‌های احراز هویت"
              rows={q.items}
              density="compact"
              stickyFirst
              onRowAction={(id) => {
                const row = q.items.find((x) => x.id === id);
                if (row)
                  void navigate({
                    to: '/users/$id',
                    params: { id: row.userId },
                    search: { tab: 'kyc' },
                  });
              }}
              columns={[
                {
                  id: 'user',
                  header: 'کاربر',
                  isRowHeader: true,
                  cell: (x) => (
                    <span className="flex flex-col">
                      <Link
                        to="/users/$id"
                        params={{ id: x.userId }}
                        search={{ tab: 'kyc' }}
                        className="font-semibold hover:underline"
                      >
                        {x.displayName ?? 'بدون نام'}
                      </Link>
                      <bdi dir="ltr" className="font-mono text-[11px] text-fg-subtle">
                        {x.phoneMasked ?? '—'}
                      </bdi>
                    </span>
                  ),
                },
                { id: 'level', header: 'سطح', cell: (x) => LEVEL_LABEL[x.level] },
                { id: 'submitted', header: 'ارسال', cell: (x) => dt(x.submittedAt) },
                {
                  id: 'state',
                  header: 'وضعیت فعلی',
                  cell: (x) => (
                    <Badge
                      tone={
                        x.status === 'TEMPORARILY_UNAVAILABLE'
                          ? 'accent'
                          : x.status === 'NEEDS_CORRECTION' || x.status === 'REJECTED'
                            ? 'negative'
                            : 'neutral'
                      }
                    >
                      {KYC1_STATUS[x.status] ?? x.status}
                    </Badge>
                  ),
                },
                {
                  id: 'issue',
                  header: 'مشکل',
                  cell: (x) =>
                    x.issue ? <Labeled label={reasonLabel(x.issue)} code={x.issue} /> : '—',
                },
                { id: 'wait', header: 'زمان انتظار', cell: (x) => formatRelative(x.waitingSince) },
                {
                  id: 'action',
                  header: 'اقدام',
                  cell: (x) => (
                    <Link
                      to="/users/$id"
                      params={{ id: x.userId }}
                      search={{ tab: 'kyc' }}
                      className="inline-flex h-8 items-center rounded-sm border border-line-strong px-3 text-[12px] font-semibold hover:bg-surface-3"
                    >
                      بررسی پرونده
                    </Link>
                  ),
                },
              ]}
              empty={
                <StateView
                  compact
                  kind="empty"
                  title="صف خالی است"
                  description="پرونده‌ای با این فیلترها در انتظار بررسی نیست."
                />
              }
            />
            <Pagination
              page={q.page}
              pageSize={q.pageSize}
              total={(q.page - 1) * q.pageSize + q.items.length + (q.hasMore ? 1 : 0)}
              onPage={setPage}
            />
          </div>
        )}
      </Remote>
    </AppContainer>
  );
}
